import cx from 'classnames';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import {
    ItineraryCard,
    ItineraryCardProps,
} from '../../../../../../design-system/components/card/itinerary/ItineraryCard';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { IconChevronDown } from '../../../../../../design-system/components/icons/ChevronDown';
import { IconSparkle } from '../../../../../../design-system/components/icons/Sparkle';
import { Typography } from '../../../../../../design-system/components/typography/Typography';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { isFacetGroup } from '../../../../../../resources/facet/domain/operations/isFacetGroup';
import { Facet } from '../../../../../../resources/facet/domain/types/facet';
import { FacetContents } from '../../../../../../resources/facetContents/domain/types/facetContents';
import { Itinerary } from '../../../../../../resources/itinerary/domain/types/Itinerary';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';

import { ProposalList } from './ProposalList/ProposalList';

import style from './Itineraries.module.css';

type Props = {
    itineraries: Array<
        ItineraryCardProps['itinerary'] & {
            highlights: Itinerary['highlights'];
            pointsOfInterest: Array<
                PickDomain<Itinerary['pointsOfInterest'][number], 'id' | 'name'>
            >;
        }
    >;
    destination: PickDomain<Destination, 'id' | 'name' | 'slug' | 'preposition'>;
    destinationItineraryCount: number;
    facetContents: PickDomain<FacetContents, 'itinerarySectionTitle'>;
    facet: PickDomain<Facet, 'id' | 'filter'>;
    agencyName?: (agency: ItineraryCardProps['itinerary']['agency']) => string;
    isABTripDesigner?: boolean;
};

const ITEMS_TO_SHOW = 12;

export function Itineraries({
    itineraries,
    facetContents,
    destination,
    destinationItineraryCount,
    agencyName,
    facet,
    isABTripDesigner = false,
}: Props) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const [numberItemsToShow, setNumberItemsToShow] = useState(ITEMS_TO_SHOW);
    const { trackEvent } = useTrackEventAmplitude();
    const proposalValues = ['experienced-guide', 'small-group', 'sustainable'] as const;

    const tripDesignerUrl = `https://trip-designer.evaneos.fr/onboarding/destination?destinationId=${destination.id}&utm_source=evaneos&utm_medium=button&utm_campaign=page-facet`;

    const handleTripDesignerClick = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli
                .pathfinderTripBuilderBannerClicked({
                    ...defaultProperties,
                    destination_id: destination.id,
                    destination_slug: destination.slug,
                })
                .promise.then(() => {
                    window.open(tripDesignerUrl, '_blank');
                })
                .catch(() => {
                    window.open(tripDesignerUrl, '_blank');
                });
        });
    };

    const handleSeeMoreClick = useCallback(() => {
        setNumberItemsToShow((numberItemsToShow) => {
            return numberItemsToShow + ITEMS_TO_SHOW;
        });

        trackEvent((ampli, defaultProperties) => {
            ampli.moreItinerariesClicked({
                ...defaultProperties,
                block: 'itineraries',
            });
        });
    }, [trackEvent]);

    return (
        <>
            <div className={cx('row', style.grid)}>
                <div className={style.headingWrapper}>
                    <Typography as="h2" scale="secondary-m-bold">
                        {facetContents.itinerarySectionTitle}
                    </Typography>

                    {isFacetGroup(facet) ? (
                        <div className={style.subtitle}>
                            <ProposalList
                                list={proposalValues.map((value) => ({
                                    text: formatMessage({
                                        id: `page.facet.group.proposalValue.${value}`,
                                    }),
                                }))}
                            />
                        </div>
                    ) : (
                        <Typography
                            as="div"
                            className={style.subtitle}
                            scale="primary-body-regular"
                        >
                            {formatMessage({ id: 'page.facet.goodToKnowDescription' })}
                        </Typography>
                    )}
                </div>

                {itineraries.map((itinerary, index) => {
                    return (
                        <div
                            key={itinerary.id}
                            data-itinerary-index={index}
                            className={cx(
                                'col-12 col-sm-6 col-lg-4 col-xl-3',
                                style.itineraryCard,
                                {
                                    hidden: index >= numberItemsToShow,
                                },
                            )}
                        >
                            <ItineraryCard
                                titleElement="h3"
                                itinerary={itinerary}
                                image={{
                                    priority: index < 4,
                                    sizes: '(min-width: 1200px) 300px, (min-width: 992px) 320px, (min-width: 576px) 280px, 100vw',
                                }}
                                trackingInformation={{
                                    click: {
                                        block: 'itineraries',
                                        card_number: index + 1,
                                        row_number: 0,
                                        tool_counter: 0,
                                    },
                                    wishListAdded: {
                                        block: 'itineraries',
                                    },
                                }}
                                agencyName={agencyName?.(itinerary.agency)}
                                showPOI={false}
                            />
                        </div>
                    );
                })}
            </div>

            <div className={style.ctaContainer}>
                {itineraries.length > numberItemsToShow ? (
                    <Button
                        className={style.seeMoreButton}
                        priority="tertiary"
                        Icon={IconChevronDown}
                        iconPosition="right"
                        onClick={handleSeeMoreClick}
                    >
                        {formatMessage({ id: 'global.cta.displayMoreItineraries' })}
                    </Button>
                ) : null}

                <div className={style.ctaButtons}>
                    <Button
                        priority="secondary-filled"
                        href={generatePath('itinerary_list', {
                            destinationSlug: destination.slug,
                        })}
                        onClick={() => {
                            trackEvent((ampli, defaultProperties) => {
                                ampli.itinerariesListClicked({
                                    ...defaultProperties,
                                    block: 'itineraries',
                                    destination_id: destination.id,
                                    destination_slug: destination.slug,
                                });
                            });
                        }}
                    >
                        {formatMessage(
                            { id: 'global.cta.listItineraries' },
                            {
                                destinationPreposition: destination.preposition,
                                destinationName: destination.name,
                                itineraryCount: destinationItineraryCount,
                            },
                        )}
                    </Button>

                    {isABTripDesigner ? (
                        <Button
                            priority="secondary"
                            Icon={IconSparkle}
                            iconPosition="left"
                            onClick={handleTripDesignerClick}
                        >
                            Générer mon itinéraire
                        </Button>
                    ) : null}
                </div>
            </div>
        </>
    );
}
